import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../components/Navbar";
import { WhatsAppShareButton } from "../components/WhatsAppShareButton";
import { FeaturedBadge } from "../components/Featured";
import { Button } from "../components/ui/button";
import { ArrowLeft, MapPin, User, Mail, Calendar, Wrench, Phone, MessageSquare, MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/services/${serviceId}`);
        setService(response.data);
      } catch (error) {
        console.error("Failed to fetch service:", error);
        navigate("/services");
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [serviceId, navigate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-CH", { day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex justify-center py-24"><div className="spinner"></div></div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="service-detail-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/services" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Torna ai servizi
        </Link>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Immagine Servizio */}
          {service.image_url ? (
            <div className="w-full h-64 sm:h-80 bg-slate-100">
              <img 
                src={service.image_url.startsWith('http') 
                  ? service.image_url 
                  : `${process.env.REACT_APP_BACKEND_URL}${service.image_url}`
                }
                alt={service.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = '/logo-transparent.png'; e.target.className = 'w-full h-full object-contain p-12'; }}
                data-testid="service-detail-image"
              />
            </div>
          ) : (
            <div className="w-full h-40 sm:h-48 bg-slate-50 border-b border-slate-100 flex items-center justify-center" data-testid="service-detail-logo">
              <img src="/logo-transparent.png" alt="LavoroTicino.ch" className="h-24 sm:h-28 object-contain" />
            </div>
          )}
          
          <div className="p-6 sm:p-8 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 font-medium rounded-full">
                {service.categoria}
              </span>
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Pubblicato il {formatDate(service.created_at)}
              </span>
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{service.title}</h1>
            <div className="mb-4 flex items-center gap-3 flex-wrap"><FeaturedBadge item={service} /><WhatsAppShareButton kind="service" item={service} /></div>
            
            <div className="flex flex-wrap gap-4 text-slate-600">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-600" />
                <span className="font-medium">{service.provider_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#DA291C]" />
                <span>{service.location}</span>
              </div>
              {(service.tariffa || service.prezzo_indicativo) && (
                <div className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-slate-400" />
                  <span className="font-medium text-emerald-600">
                    {service.tariffa || service.prezzo_indicativo}
                    {!(service.tariffa || service.prezzo_indicativo || '').toLowerCase().includes('chf') && ' CHF'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Descrizione del servizio</h2>
            <p className="text-slate-600 whitespace-pre-wrap">{service.description}</p>
          </div>

          {/* Sezione Contatti */}
          <div className="p-6 sm:p-8 bg-slate-50 border-t border-slate-100">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Contatta il fornitore</h3>
            <div className="flex flex-wrap gap-3">
              {/* Messaggistica Interna */}
              {(service.contact_methods?.includes("internal") || service.contact_method === "internal" || (!service.contact_methods && !service.contact_method)) && user && (
                <Link to={`/messages?to=${service.provider_id}`}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Messaggio
                  </Button>
                </Link>
              )}
              
              {/* WhatsApp */}
              {(service.contact_methods?.includes("whatsapp") || service.contact_method === "whatsapp") && service.provider_phone && (
                <a href={`https://wa.me/${service.provider_phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
              )}
              
              {/* Email */}
              {(service.contact_methods?.includes("email")) && service.provider_email && (
                <a href={`mailto:${service.provider_email}`}>
                  <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <Mail className="w-4 h-4 mr-2" />
                    {service.provider_email}
                  </Button>
                </a>
              )}
              
              {/* Telefono */}
              {(service.contact_methods?.includes("phone")) && service.provider_phone && (
                <a href={`tel:${service.provider_phone}`}>
                  <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                    <Phone className="w-4 h-4 mr-2" />
                    {service.provider_phone}
                  </Button>
                </a>
              )}
              
              {/* Fallback se nessun metodo specificato */}
              {!service.contact_methods?.length && !service.contact_method && service.provider_email && (
                <a href={`mailto:${service.provider_email}`}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Mail className="w-4 h-4 mr-2" />
                    Contatta via Email
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
